
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { Loader2, Clock, CheckCircle, AlertCircle, Wifi, AlertTriangle, RefreshCw, Eye, Sparkles, Image } from "lucide-react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";

interface LogMessage {
  timestamp?: string;
  message: string;
}

interface ModernProgressProps {
  processed: number;
  total: number;
  stage?: string;
  status: "idle" | "pending" | "processing" | "completed" | "error";
  error?: string;
  currentAction?: string;
  logMessages?: (string | LogMessage)[];
  className?: string;
  mode?: 'import' | 'generate' | 'translate';
  collectionId?: string;
}

export const ModernProgress = ({
  processed,
  total,
  stage = "processing",
  status,
  error,
  currentAction,
  logMessages = [],
  className,
  mode,
  collectionId
}: ModernProgressProps) => {
  // Stabilize percentage calculation to prevent infinite loops
  const percentage = useMemo(() => {
    return total > 0 ? Math.min(Math.max(Math.round((processed / total) * 100), 0), 100) : 0;
  }, [processed, total]);
  
  const [animatedPercent, setAnimatedPercent] = useState(percentage);
  const [currentLogIndex, setCurrentLogIndex] = useState<number>(0);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [realtimeActive, setRealtimeActive] = useState<boolean>(false);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  
  const logIntervalRef = useRef<number | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const hasLogs = logMessages && logMessages.length > 0;

  // Memoize internal action type to prevent recalculation
  const internalAction = useMemo((): 'generating' | 'improving' | 'analyzing' | 'processing' => {
    if (!currentAction) return 'processing';
    
    const actionLower = currentAction.toLowerCase();
    const stageLower = stage?.toLowerCase() || '';
    
    // Check for visual analysis first
    if (stageLower.includes('visual') || actionLower.includes('analyz') || 
        actionLower.includes('visual') || actionLower.includes('image') ||
        stageLower === 'visual-analysis') {
      return 'analyzing';
    }
    
    if (actionLower.includes('generat') || actionLower.includes('creat')) {
      return 'generating';
    }
    
    if (actionLower.includes('improv') || actionLower.includes('enhanc') || actionLower.includes('refin')) {
      return 'improving';
    }
    
    return 'processing';
  }, [currentAction, stage]);

  // Memoize validation warnings extraction
  const extractedWarnings = useMemo(() => {
    return logMessages
      .map(msg => typeof msg === 'string' ? msg : msg.message)
      .filter(msg => msg.includes('Validation issues'))
      .map(msg => {
        if (msg.includes('Translation appears unchanged from original')) {
          return 'Translation appears similar to original - may need manual review';
        }
        return 'Quality check detected potential issues';
      });
  }, [logMessages]);

  // Update validation warnings only when they actually change
  useEffect(() => {
    if (JSON.stringify(extractedWarnings) !== JSON.stringify(validationWarnings)) {
      setValidationWarnings(extractedWarnings);
    }
  }, [extractedWarnings, validationWarnings]);

  // Stabilized animation effect - only trigger when percentage actually changes
  useEffect(() => {
    if (percentage !== animatedPercent) {
      // Clear any existing timeout
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      
      setLastUpdateTime(new Date());
      setRealtimeActive(true);
      
      // Clear existing realtime timeout
      if (realtimeTimeoutRef.current) {
        clearTimeout(realtimeTimeoutRef.current);
      }
      
      // Reset realtime indicator after 2 seconds
      realtimeTimeoutRef.current = setTimeout(() => setRealtimeActive(false), 2000);
      
      // Animate to new percentage
      const step = percentage > animatedPercent ? 1 : -1;
      animationTimeoutRef.current = setTimeout(() => {
        setAnimatedPercent(prev => {
          if ((step > 0 && prev < percentage) || (step < 0 && prev > percentage)) {
            return prev + step;
          }
          return percentage;
        });
      }, 20);
    }
  }, [percentage, animatedPercent]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (realtimeTimeoutRef.current) {
        clearTimeout(realtimeTimeoutRef.current);
      }
    };
  }, []);

  // Memoize non-validation logs to prevent recalculation
  const nonValidationLogs = useMemo(() => {
    return logMessages.filter(msg => {
      const message = typeof msg === 'string' ? msg : msg.message;
      return !message.includes('Validation issues');
    });
  }, [logMessages]);

  // Stabilized log cycling effect
  useEffect(() => {
    if (logIntervalRef.current !== null) {
      clearInterval(logIntervalRef.current);
      logIntervalRef.current = null;
    }
    
    if (status === 'processing' && nonValidationLogs.length > 0) {
      setCurrentLogIndex(0);
      
      const interval = setInterval(() => {
        setCurrentLogIndex(prevIndex => {
          return (prevIndex + 1) % nonValidationLogs.length;
        });
      }, 3000);
      
      logIntervalRef.current = interval as unknown as number;
    }
    
    return () => {
      if (logIntervalRef.current !== null) {
        clearInterval(logIntervalRef.current);
      }
    };
  }, [status, nonValidationLogs.length]); // Only depend on length, not the array itself

  // Generate detailed text based on current state - show only ONE progress indicator
  const getDetailedProcessingText = useCallback(() => {
    if (percentage === 100 || status === 'completed') {
      return internalAction === 'analyzing' ? "Visual analysis completed" : "Processing completed successfully";
    }
    
    if (!currentAction) {
      if (internalAction === 'analyzing') {
        return "Analyzing product images...";
      }
      return mode === 'generate' ? "Generating descriptions..." : "Processing...";
    }
    
    // Use the clean action text without adding redundant counts
    // The progress bar already shows the visual progress
    return currentAction;
  }, [percentage, status, currentAction, mode, internalAction]);

  // Memoize current log message
  const getCurrentLogMessage = useCallback(() => {
    if (nonValidationLogs.length > 0 && currentLogIndex < nonValidationLogs.length) {
      const message = nonValidationLogs[currentLogIndex];
      if (typeof message === 'string') {
        return message.replace(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*?\s/, ''); // Strip timestamp
      }
      
      if (typeof message === 'object' && message !== null && 'message' in message) {
        return String(message.message).replace(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*?\s/, '');
      }
      
      return String(message);
    }
    return getDetailedProcessingText();
  }, [nonValidationLogs, currentLogIndex, getDetailedProcessingText]);
  
  const currentLog = getCurrentLogMessage();
  const isCompleted = percentage >= 100 && status === 'completed';
  const isRegenerating = stage === 'regenerating' || stage === 'preparing' && status === 'processing';
  
  // Simplified logic: no more error states shown to user
  const showRegenerating = isRegenerating || stage === 'regenerating' || stage.includes('regenerat') || stage.includes('prepar');

  const getProcessTitle = useCallback(() => {
    if (internalAction === 'analyzing') return 'Visual Analysis Progress';
    if (mode === 'translate') return 'Translation Progress';
    if (mode === 'generate') return 'Generation Progress';
    if (mode === 'import') return 'Import Progress';
    return 'Progress';
  }, [mode, internalAction]);

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">{getProcessTitle()}</div>
          {internalAction === 'analyzing' && (status === 'processing' || isRegenerating) && (
            <Eye className="h-3 w-3 animate-pulse text-purple-500" />
          )}
          {internalAction === 'generating' && (status === 'processing' || isRegenerating) && (
            <Sparkles className="h-3 w-3 animate-spin text-blue-500" />
          )}
          {internalAction === 'improving' && (status === 'processing' || isRegenerating) && (
            <RefreshCw className="h-3 w-3 animate-spin text-orange-500" />
          )}
          {internalAction === 'processing' && (status === 'processing' || isRegenerating) && (
            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
          )}
          {isCompleted && <CheckCircle className="h-3 w-3 text-green-500" />}
          {validationWarnings.length > 0 && status !== 'error' && (
            <AlertTriangle className="h-3 w-3 text-amber-500" />
          )}
        </div>
        <div className="text-sm font-medium flex items-center gap-2">
          <span>{animatedPercent}%</span>
          {realtimeActive && <Wifi className="h-3 w-3 text-green-500 animate-pulse" />}
          {status === 'completed' && validationWarnings.length > 0 && (
            <span className="text-xs text-amber-600">with notices</span>
          )}
        </div>
      </div>
      
       <Progress 
        value={animatedPercent} 
        className={cn(
          "transition-all duration-300",
          isCompleted && "bg-muted [&>div]:bg-green-500",
          isRegenerating && "bg-muted [&>div]:bg-orange-400",
          internalAction === 'analyzing' && "bg-muted [&>div]:bg-purple-400",
          internalAction === 'generating' && "bg-muted [&>div]:bg-blue-400", 
          validationWarnings.length > 0 && status !== 'error' && "[&>div]:bg-amber-400",
          realtimeActive && "[&>div]:animate-pulse"
        )}
      />
      
      <div className="flex justify-between items-center text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="font-mono">
            {isCompleted ? (
              validationWarnings.length > 0 
                ? `Processing complete with ${validationWarnings.length} quality notice(s)`
                : "Processing complete"
            ) : isRegenerating ? (
              mode === 'translate' ? 
                (stage === 'preparing' ? "Preparing to translate..." : "Translating...") :
                (stage === 'preparing' ? "Preparing to regenerate..." : "Regenerating translations...")
            ) : status === 'processing' || status === 'pending' ? (
              getDetailedProcessingText()
            ) : (
               // Always show positive messaging - check progress percentage first
                showRegenerating ? (mode === 'translate' ? "Translating..." : "Regenerating translations...") :
               status === 'completed' ? "Processing complete" : 
               percentage > 0 ? getDetailedProcessingText() : "Initializing..."
            )}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <span>{processed} / {total}</span>
          <div className="flex items-center gap-1">
            {realtimeActive ? (
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            ) : (
              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
            )}
            <span className="text-xs">
              {lastUpdateTime.toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Processing logs section hidden - info shown above */}

      {/* Real-time indicator */}
      {status === 'processing' && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-2 h-2 rounded-full",
              realtimeActive ? "bg-green-500 animate-pulse" : "bg-gray-300"
            )}></div>
            <span>Real-time updates {realtimeActive ? 'active' : 'monitoring'}</span>
          </div>
          <span>Last: {lastUpdateTime.toLocaleTimeString()}</span>
        </div>
      )}
    </div>
  );
};
